import { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch } from '@/lib/api';
import { currentSupabaseEnv } from '@/lib/supabase';
import { GRID_SELL_RATE_PER_KWH } from '@/types/reports';
import type { TimeGranularity, GroupTimeSeriesPoint } from '@/types/reports';

interface UseEnergyTimeSeriesOptions {
  groupIds: string[];
  modelCodes?: string[];
  industryTypes?: string[];
  /** Only these units (Reports → Unit Status click-to-focus); null/absent = no unit filter */
  unitIds?: string[] | null;
  startDate?: Date | null;
  endDate?: Date | null;
  granularity: TimeGranularity;
  enabled?: boolean;
}

interface UseEnergyTimeSeriesReturn {
  timeSeries: GroupTimeSeriesPoint[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// Module-level dedup + short-lived cache, shared by every hook instance.
// Six /reports sections mount this hook with identical params; without
// sharing, each issued its own HTTP call (Sentry HOLLYWOODTRUCKS-API-4).
const CACHE_TTL_MS = 30_000;
const resultCache = new Map<string, { data: GroupTimeSeriesPoint[]; expiresAt: number }>();
const inFlight = new Map<string, Promise<GroupTimeSeriesPoint[]>>();

// Bumped on every clear so requests already in flight when the cache was
// cleared (e.g. signOut mid-fetch) cannot write their result back under
// the user-agnostic key and expose the previous user's data.
let cacheGeneration = 0;

/** Drops cached time-series data (e.g. on logout, or between tests). */
export function clearEnergyTimeSeriesCache(): void {
  cacheGeneration++;
  resultCache.clear();
  inFlight.clear();
}

async function fetchAndMapTimeSeries(query: string): Promise<GroupTimeSeriesPoint[]> {
  const response = await authenticatedFetch(`/api/v1/energy/time-series?${query}`);

  if (!response.ok) {
    throw new Error('Failed to fetch time series data');
  }

  const data = await response.json();

  // Map API response to GroupTimeSeriesPoint format
  return (data.timeSeries || []).map((point: {
    date: string;
    periodLabel: string;
    solarKwh?: number;
    gridKwh?: number;
    generatorKwh?: number;
    offGridPercent?: number;
    co2Reduced?: number;
    costSaved?: number;
    gridSellRevenue?: number;
    gridCost?: number;
    gridExportKwh?: number;
    unitsActive: number;
  }) => ({
    date: point.date,
    periodLabel: point.periodLabel,
    solarKwh: point.solarKwh ?? 0,
    gridKwh: point.gridKwh ?? 0,
    gridExportKwh: point.gridExportKwh ?? 0,
    generatorKwh: point.generatorKwh ?? 0,
    offGridPercent: point.offGridPercent ?? 0,
    co2Reduced: point.co2Reduced ?? 0,
    costSaved: point.costSaved ?? 0,
    // Prod: the DB's revenue figure is authoritative and a missing field
    // is 0 — never re-derived at a default rate. Demo keeps its simulated
    // export-kWh revenue fallback for every consumer of this hook.
    gridSellRevenue: point.gridSellRevenue
      ?? (currentSupabaseEnv === 'demo' && point.gridExportKwh
        ? point.gridExportKwh * GRID_SELL_RATE_PER_KWH
        : 0),
    // Absent (not 0) until the database has the gridCost field, so consumers
    // can tell "no grid cost yet" from "$0 of grid import".
    gridCost: typeof point.gridCost === 'number' ? point.gridCost : undefined,
    unitsActive: point.unitsActive,
  }));
}

function fetchTimeSeriesShared(query: string, force: boolean): Promise<GroupTimeSeriesPoint[]> {
  if (!force) {
    const cached = resultCache.get(query);
    if (cached && cached.expiresAt > Date.now()) {
      return Promise.resolve(cached.data);
    }
    const pending = inFlight.get(query);
    if (pending) {
      return pending;
    }
  }

  const generation = cacheGeneration;
  const promise = fetchAndMapTimeSeries(query)
    .then((data) => {
      if (generation === cacheGeneration) {
        resultCache.set(query, { data, expiresAt: Date.now() + CACHE_TTL_MS });
      }
      return data;
    })
    .finally(() => {
      if (inFlight.get(query) === promise) {
        inFlight.delete(query);
      }
    });

  inFlight.set(query, promise);
  return promise;
}

/**
 * Fetches aggregated energy time-series data for charting based on selected groups and filters.
 *
 * @param groupIds - List of group IDs to include. `__unassigned_<companyId>__`
 *   sentinels are passed through — the backend splits them into
 *   p_unassigned_company_ids so unassigned units count (PR #593).
 * @param modelCodes - Optional list of model codes to filter results.
 * @param industryTypes - Optional list of industry types to filter results.
 * @param startDate - Optional start of the date range to fetch (inclusive).
 * @param endDate - Optional end of the date range to fetch (inclusive).
 * @param granularity - Time granularity for the series (e.g., daily, monthly).
 * @param enabled - When `false`, clears results and prevents fetching.
 * @returns An object with:
 *  - `timeSeries`: the fetched and normalized array of `GroupTimeSeriesPoint` entries,
 *  - `isLoading`: `true` while a fetch is in progress,
 *  - `error`: the last fetch error or `null`,
 *  - `refetch`: function to re-run the fetch manually.
 */
export function useEnergyTimeSeries({
  groupIds,
  modelCodes = [],
  industryTypes = [],
  unitIds = null,
  startDate,
  endDate,
  granularity,
  enabled = true,
}: UseEnergyTimeSeriesOptions): UseEnergyTimeSeriesReturn {
  const [timeSeries, setTimeSeries] = useState<GroupTimeSeriesPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Derive stable string keys from the array props. Callers build these arrays
  // inline each render, so depending on the raw refs recreates fetchTimeSeries
  // every render and refires the effect in a loop. Joined strings only change
  // when the values change. __unassigned_* sentinels stay in: the route
  // parses them server-side, so stripping them here dropped unassigned units.
  const groupIdsKey = groupIds.filter(Boolean).join(',');
  const modelCodesKey = modelCodes.join(',');
  const industryTypesKey = industryTypes.join(',');
  const unitIdsKey = unitIds?.join(',') ?? '';

  const fetchTimeSeries = useCallback(async (force = false) => {
    if (!enabled || !groupIdsKey) {
      setTimeSeries([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      params.set('groupIds', groupIdsKey);
      if (modelCodesKey) {
        params.set('modelCodes', modelCodesKey);
      }
      if (industryTypesKey) {
        params.set('industryTypes', industryTypesKey);
      }
      if (unitIdsKey) {
        params.set('unitIds', unitIdsKey);
      }
      if (startDate) {
        params.set('startDate', startDate.toISOString().split('T')[0]);
      }
      if (endDate) {
        params.set('endDate', endDate.toISOString().split('T')[0]);
      }
      params.set('granularity', granularity);

      setTimeSeries(await fetchTimeSeriesShared(params.toString(), force));
    } catch (err) {
      console.error('[useEnergyTimeSeries] Error:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setTimeSeries([]);
    } finally {
      setIsLoading(false);
    }
  }, [groupIdsKey, modelCodesKey, industryTypesKey, unitIdsKey, startDate, endDate, granularity, enabled]);

  useEffect(() => {
    fetchTimeSeries();
  }, [fetchTimeSeries]);

  const refetch = useCallback(() => {
    fetchTimeSeries(true);
  }, [fetchTimeSeries]);

  return {
    timeSeries,
    isLoading,
    error,
    refetch,
  };
}
